import React from 'react';
import { Pencil } from 'lucide-react';
import CompactSelectPicker from './CompactSelectPicker';
import { toPickerOptions, readOptions } from '../../utils/variableOptions';

/**
 * The value field for a variable that remembers alternatives.
 *
 * One component for all four places a variable is edited — the request's
 * Variables panel, the Environments modal, collection variables and
 * workspace/global variables — so the affordance cannot drift between them. It
 * is the app's own dropdown (portaled menu, searchable once the list is long),
 * never a native select.
 *
 * Picking swaps the ACTIVE value. The alternatives are untouched, and what the
 * variable resolves to is still a single string.
 */
export default function VariableValuePicker({
  variable,
  value,
  onChange,
  className = '',
  menuClassName = '',
  disabled = false,
  ariaLabel,
  /**
   * Called when the user asks to edit the list itself. The menu offers the
   * alternatives and no way to change them, so the only route to "drop this
   * one" was to find the variable in a table somewhere else — which is the
   * table this picker exists to save you from opening (owner, 23.09.2026).
   * Omitted where there is nowhere to open.
   */
  onEditOptions,
}) {
  const options = readOptions(variable);
  return (
    <CompactSelectPicker
      className={`var-value-picker ${className}`.trim()}
      menuClassName={menuClassName}
      value={value}
      options={toPickerOptions(variable)}
      disabled={disabled}
      // A short list is faster to read than to search; a long one is not.
      searchable={options.length > 8}
      menuMinWidth={200}
      aria-label={ariaLabel}
      renderFooter={
        onEditOptions
          ? ({ close }) => (
              <button
                type="button"
                className="var-value-picker__edit"
                onClick={() => {
                  // Close first: what this opens is a popup positioned at the
                  // cell, and leaving the menu over it is how you get two
                  // floating things arguing about the same click.
                  close();
                  onEditOptions();
                }}
              >
                <Pencil size={11} aria-hidden />
                Edit options…
              </button>
            )
          : null
      }
      onChange={onChange}
    />
  );
}
